import { Download, Search, RotateCw } from 'lucide-react'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { useLocation, useParams } from 'react-router-dom'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import InputDatePicker from '../../../components/Input/DatePicker/InputDatePicker'
import api from '../../../config/AxiosInstance'
import {
  convertDateWithoutTime,
  convertDateWithTime
} from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import BreadCrumb from '../../../components/Label/Breadcrumb/index'

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

function DetailSlotPage() {
  const { state } = useLocation()
  const { slotId } = useParams()
  const [filter, setFilter] = useState({
    date_start: moment().startOf('month').format('YYYY-MM-DD'),
    date_end: moment().format('YYYY-MM-DD'),
    q: ''
  })
  const [debouncedQuery, setDebouncedQuery] = useState(filter.q)

  const [stockCards, setStockCards] = useState([])
  const [loading, setLoading] = useState({
    stockCard: false,
    download: false
  })
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(filter.q)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [filter.q])

  const handleChangeInput = (key, value) => {
    setFilter((prev) => ({
      ...prev,
      [key]: value
    }))
    if (key === 'date_start' || key === 'date_end') {
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }
  }

  const handleReset = () => {
    setFilter({
      date_start: moment().startOf('month').format('YYYY-MM-DD'),
      date_end: moment().format('YYYY-MM-DD'),
      q: ''
    })
    setDebouncedQuery('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleSearch = useCallback(async () => {
    if (!filter.date_start || !filter.date_end) {
      setStockCards([])
      setMeta((prev) => ({ ...prev, total_row: 0, total_page: 0 }))
      return
    }

    try {
      setLoading((prev) => ({ ...prev, stockCard: true }))
      const params = {
        start_date: convertDateWithTime(filter.date_start, 'YYYY-MM-DD'),
        end_date: convertDateWithTime(filter.date_end, 'YYYY-MM-DD'),
        q: debouncedQuery,
        page: meta.current_page,
        per_page: meta.per_page
      }
      const res = await api.get(`/master/slot-detail/${slotId}`, {
        params
      })
      const { status, data } = res
      if (status === 200) {
        setStockCards(data.data)
        setMeta(data.metadata)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading((prev) => ({ ...prev, stockCard: false }))
    }
  }, [
    slotId,
    debouncedQuery,
    filter.date_start,
    filter.date_end,
    meta.current_page,
    meta.per_page
  ])

  useEffect(() => {
    handleSearch()
  }, [handleSearch])

  const handleDownload = async () => {
    try {
      setLoading((prev) => ({ ...prev, download: true }))
      const params = {
        start_date: convertDateWithTime(filter.date_start, 'YYYY-MM-DD'),
        end_date: convertDateWithTime(filter.date_end, 'YYYY-MM-DD'),
        q: debouncedQuery,
        export: 1
      }
      const res = await api.get(`/master/slot-detail/${slotId}`, {
        responseType: 'arraybuffer',
        params
      })
      const { status, data } = res
      if (status === 200) {
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `download-stock-card-slot-detail-${state?.code}.xlsx`
        )
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        throw new Error('Something went wrong')
      }
    } catch (error) {
      const err =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      Common.showPopUpErrorMessage(err)
    } finally {
      setLoading((prev) => ({ ...prev, download: false }))
    }
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const renderPack = (data) => {
    return (
      <div className={data.qty < 0 ? 'text-danger' : ''}>
        {`${data.qty} ${data.qty_uom}`}
      </div>
    )
  }

  const renderResult = (data) => {
    return (
      <div>
        {data.result} {`${data.result === 0 ? '' : ` ${data.qty_uom}`}`}
      </div>
    )
  }

  const columns = [
    {
      key: 'date',
      label: 'Date',
      component: (data) => convertDateWithoutTime(data.date)
    },
    { key: 'item_name', label: 'Item' },
    { key: 'batch', label: 'Batch', className: 'text-end' },
    { key: 'doc', label: 'Doc', className: 'text-end' },
    {
      key: 'pack',
      label: 'In/Out',
      className: 'text-end',
      component: (data) => renderPack(data)
    },
    {
      key: 'current_pack',
      label: 'Result',
      className: 'text-end',
      component: (data) => renderResult(data)
    }
  ]

  return (
    <Container fluid className="px-4">
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <BreadCrumb
            label={state?.code || ''}
            link="/master/slot-details"
            labelSub="STOCK CARD"
            back="Slot Detail"
            stateValue={state}
          />
        </Col>
        <Col className="text-end d-flex gap-2 justify-content-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem' }}
          >
            <RotateCw size={16} className="me-2" />
            Reset Filters
          </Button>
          <Button
            variant="primary"
            className="rounded px-4 btn-primary-orange"
            onClick={handleDownload}
            disabled={loading.download || stockCards.length === 0}
            style={{ fontSize: '0.85rem' }}
          >
            <Download size={16} className="me-2" />
            {loading.download ? 'Downloading...' : 'Download'}
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Start Date</Form.Label>
              <div
                style={FormControlStyle}
                className="d-flex align-items-center px-2"
              >
                <InputDatePicker
                  value={filter.date_start}
                  onChange={(date) => handleChangeInput('date_start', date)}
                  clearIcon
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>End Date</Form.Label>
              <div
                style={FormControlStyle}
                className="d-flex align-items-center px-2"
              >
                <InputDatePicker
                  value={filter.date_end}
                  onChange={(date) => handleChangeInput('date_end', date)}
                  clearIcon
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by Item Name, Batch, or Doc..."
                  value={filter.q}
                  onChange={(e) => handleChangeInput('q', e.target.value)}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Row>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                {columns.map((column) => (
                  <th
                    className={`p-2 align-middle ${column.className || ''}`}
                    style={tableHeaderStyle}
                    key={column.key}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading.stockCard && (
                <tr>
                  <td colSpan={columns.length} className="p-3 text-center">
                    <Loader />
                  </td>
                </tr>
              )}
              {!loading.stockCard && stockCards.length === 0 && (
                <RowEmpty colSpan={columns.length} />
              )}
              {!loading.stockCard &&
                stockCards?.map((stockCard, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`p-2 align-middle ${column.className || ''}`}
                      >
                        {column.component
                          ? column.component(stockCard)
                          : stockCard[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={columns.length} className="p-3 border-top-0">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>
    </Container>
  )
}

export default DetailSlotPage
